import { parseXrkBuffer, sessionToGpx, validateTelemetryBuffer } from '@kangaroo/mychron/xrk';

import type { MyChronGpsPoint } from './telemetrySession';
import { buildMyChronSessionFromGps } from './gpsLapDetection';

export type XrzToGpxResult = {
  gpx: string;
  fileName: string;
  sampleCount: number;
  sessionName: string;
};

export type TelemetryPreviewLoadResult = {
  gps: MyChronGpsPoint[];
  sessionName: string;
  startedAtIso?: string | null;
  gpx?: XrzToGpxResult;
};

function mapGpsSamples(samples: Array<{
  timecodeMs: number;
  lat: number;
  lon: number;
  alt: number;
  speed: number;
  fix?: number;
  satellites?: number;
}>): MyChronGpsPoint[] {
  return samples.map((sample) => ({
    timecodeMs: sample.timecodeMs,
    lat: sample.lat,
    lon: sample.lon,
    alt: sample.alt,
    speed: sample.speed,
    fix: sample.fix,
    satellites: sample.satellites,
  }));
}

function haversineMeters(lat1: number, lon1: number, lat2: number, lon2: number) {
  const earthRadiusM = 6_371_000;
  const lat1Rad = (lat1 * Math.PI) / 180;
  const lat2Rad = (lat2 * Math.PI) / 180;
  const deltaLat = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(deltaLat / 2) ** 2
    + Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLon / 2) ** 2;

  return 2 * earthRadiusM * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function parseGpxText(text: string, sessionName = 'gpx-session'): TelemetryPreviewLoadResult {
  const points: Array<{ lat: number; lon: number; alt: number; timeMs: number }> = [];

  for (const match of text.matchAll(/<trkpt lat="([^"]+)" lon="([^"]+)">([\s\S]*?)<\/trkpt>/g)) {
    const lat = Number(match[1]);
    const lon = Number(match[2]);
    const inner = match[3] ?? '';
    const eleMatch = /<ele>([^<]+)<\/ele>/.exec(inner);
    const timeMatch = /<time>([^<]+)<\/time>/.exec(inner);

    if (!Number.isFinite(lat) || !Number.isFinite(lon) || !timeMatch) {
      continue;
    }

    const timeMs = Date.parse(timeMatch[1]!);
    if (!Number.isFinite(timeMs)) {
      continue;
    }

    points.push({
      lat,
      lon,
      alt: eleMatch ? Number(eleMatch[1]) : 0,
      timeMs,
    });
  }

  if (points.length < 2) {
    throw new Error('No usable GPS track points found in GPX.');
  }

  const baseMs = points[0]!.timeMs;
  const startedAtIso = new Date(baseMs).toISOString();
  const gps: MyChronGpsPoint[] = points.map((point, index) => {
    const previous = points[index - 1];
    let speed = 0;

    if (previous) {
      const deltaSec = (point.timeMs - previous.timeMs) / 1000;
      if (deltaSec > 0) {
        speed = haversineMeters(previous.lat, previous.lon, point.lat, point.lon) / deltaSec;
      }
    }

    return {
      timecodeMs: point.timeMs - baseMs,
      lat: point.lat,
      lon: point.lon,
      alt: Number.isFinite(point.alt) ? point.alt : 0,
      speed,
    };
  });

  return {
    gps,
    sessionName: sessionName.replace(/\.gpx$/i, '') || 'gpx-session',
    startedAtIso,
  };
}

export async function parseGpxFile(file: File): Promise<TelemetryPreviewLoadResult> {
  const text = await file.text();
  return parseGpxText(text, file.name.replace(/\.gpx$/i, '') || 'gpx-session');
}

export async function convertXrzFileToGpx(file: File): Promise<XrzToGpxResult> {
  const buffer = Buffer.from(await file.arrayBuffer());
  const validationError = validateTelemetryBuffer(buffer);
  if (validationError) {
    throw new Error(validationError);
  }

  const parsed = parseXrkBuffer(buffer);
  if (parsed.gps.length === 0) {
    throw new Error('No GPS samples found in this file.');
  }

  const baseName = file.name.replace(/\.(xrz|xrk|hrz|hrk|drk)$/i, '') || 'mychron-session';
  const gpx = sessionToGpx(parsed, { name: baseName });

  return {
    gpx,
    fileName: `${baseName}.gpx`,
    sampleCount: parsed.gps.length,
    sessionName: baseName,
  };
}

export async function loadTelemetryPreviewFromFile(file: File): Promise<TelemetryPreviewLoadResult> {
  if (/\.gpx$/i.test(file.name)) {
    return parseGpxFile(file);
  }

  const buffer = Buffer.from(await file.arrayBuffer());
  const validationError = validateTelemetryBuffer(buffer);
  if (validationError) {
    throw new Error(validationError);
  }

  const parsed = parseXrkBuffer(buffer);
  if (parsed.gps.length === 0) {
    throw new Error('No GPS samples found in this file.');
  }

  const baseName = file.name.replace(/\.(xrz|xrk|hrz|hrk|drk)$/i, '') || 'mychron-session';
  const gps = mapGpsSamples(parsed.gps);

  return {
    gps,
    sessionName: baseName,
    gpx: {
      gpx: sessionToGpx(parsed, { name: baseName }),
      fileName: `${baseName}.gpx`,
      sampleCount: gps.length,
      sessionName: baseName,
    },
  };
}

export async function buildMyChronSessionFromGpxFile(file: File) {
  const parsed = await parseGpxFile(file);
  return {
    sessionName: parsed.sessionName,
    session: buildMyChronSessionFromGps(parsed.gps, {
      sessionName: parsed.sessionName,
      startedAtIso: parsed.startedAtIso ?? null,
    }),
  };
}

export function downloadGpxFile(gpx: string, fileName: string) {
  const blob = new Blob([gpx], { type: 'application/gpx+xml' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}
